import { HttpRequest, HttpResponse, WebSocketRequestMessage, WebSocketResponseMessage } from '../../proto/generated/websocket'
import { ensureValidAccessToken } from '../auth/tokenRefresh'
import { currentAdapter } from '../apis/axiosClient'
import { BackendProvider } from '../../adapters/types'

/**
 * Request/response calls over the app websocket.
 *
 * Small REST endpoints are being moved onto the socket: the client sends a
 * `WebSocketRequestMessage.httpRequest` with a fresh request id and the server answers with a
 * `WebSocketResponseMessage.httpResponse` carrying the same id. This module owns the
 * correlation, timeouts and the frame listener; callers only see a promise.
 *
 * The transport is registered by WebSocketProvider so plain modules (services, utils) can use it
 * without React context. When the socket is unavailable callers should fall back to HTTP.
 */

type FrameListener = (data: Uint8Array, message: WebSocketResponseMessage) => void

export interface SocketHttpTransport {
    sendMessage: (message: Uint8Array) => void
    addListener: (listener: FrameListener) => void
    removeListener: (listener: FrameListener) => void
    getSocket: () => WebSocket | null
}

export class SocketHttpError extends Error {
    /** HTTP style status from the server, or 0 when the request never got an answer. */
    readonly status: number

    constructor(message: string, status: number) {
        super(message)
        this.name = 'SocketHttpError'
        this.status = status
    }
}

type PendingRequest = {
    resolve: (response: HttpResponse) => void
    reject: (error: SocketHttpError) => void
    timer: ReturnType<typeof setTimeout>
}

const DEFAULT_TIMEOUT_MS = 10_000

let transport: SocketHttpTransport | null = null
let requestCounter = 0
const pendingRequests = new Map<string, PendingRequest>()

const nextRequestId = (): string => {
    requestCounter += 1
    const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2)
    return `${requestCounter}-${random}`
}

const onFrame = (_data: Uint8Array, message: WebSocketResponseMessage) => {
    if (pendingRequests.size === 0) return
    const response: HttpResponse | undefined = message.httpResponse
    if (!response) return
    settle(response.requestId, (pending) => {
        if (response!.status >= 200 && response!.status < 300) {
            pending.resolve(response!)
        } else {
            pending.reject(new SocketHttpError(response!.error || `Request failed with status ${response!.status}`, response!.status))
        }
    })
}

const settle = (requestId: string, complete: (pending: PendingRequest) => void) => {
    const pending = pendingRequests.get(requestId)
    if (!pending) return
    clearTimeout(pending.timer)
    pendingRequests.delete(requestId)
    if (pendingRequests.size === 0) {
        transport?.removeListener(onFrame)
    }
    complete(pending)
}

const rejectAllPending = (reason: string) => {
    Array.from(pendingRequests.keys()).forEach((requestId) => {
        settle(requestId, (pending) => pending.reject(new SocketHttpError(reason, 0)))
    })
}

/**
 * Installs (or clears, with null) the socket used for request/response calls. Called by
 * WebSocketProvider whenever the underlying socket changes; anything still in flight on the
 * previous socket is rejected so callers can fall back to HTTP right away.
 */
export function registerSocketHttpTransport(next: SocketHttpTransport | null): void {
    if (transport && pendingRequests.size > 0) {
        transport.removeListener(onFrame)
    }
    rejectAllPending('Websocket transport changed')
    transport = next
}

/** True when a request sent now will actually go over the socket to the tick2trade backend. */
export function isSocketHttpAvailable(): boolean {
    if (!transport) return false
    if (currentAdapter.getProvider() !== BackendProvider.TICK2TRADE) return false
    return transport.getSocket()?.readyState === WebSocket.OPEN
}

/**
 * Sends one request over the socket and resolves with the matching response.
 * Rejects with SocketHttpError when the socket is unavailable, the server answers with a
 * non-2xx status, or no answer arrives within `timeoutMs`.
 */
export async function sendSocketHttpRequest(
    body: Omit<HttpRequest, 'requestId'>,
    options: { timeoutMs?: number } = {},
): Promise<HttpResponse> {
    const activeTransport = transport
    if (!activeTransport || !isSocketHttpAvailable()) {
        throw new SocketHttpError('Websocket is not connected', 0)
    }
    const authToken = await ensureValidAccessToken()
    if (!authToken) {
        throw new SocketHttpError('No valid access token', 401)
    }
    const requestId = nextRequestId()
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS

    return new Promise<HttpResponse>((resolve, reject) => {
        const timer = setTimeout(() => {
            settle(requestId, (pending) => pending.reject(new SocketHttpError(`Websocket request timed out after ${timeoutMs}ms`, 0)))
        }, timeoutMs)
        if (pendingRequests.size === 0) {
            activeTransport.addListener(onFrame)
        }
        pendingRequests.set(requestId, { resolve, reject, timer })

        const request = WebSocketRequestMessage.fromPartial({
            authToken,
            httpRequest: { ...body, requestId },
        })
        try {
            activeTransport.sendMessage(WebSocketRequestMessage.encode(request).finish())
        } catch (error) {
            settle(requestId, (pending) => pending.reject(new SocketHttpError(`Failed to send websocket request: ${String(error)}`, 0)))
        }
    })
}
